import React, { useState, useEffect, useRef } from 'react';
import { Plus, Upload, X, AlertTriangle, ArrowUpRight, ArrowDownRight, FileText, Truck, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import api from '../../services/api';
import { toast } from 'sonner';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const initialForm = {
  type: 'expense',
  classification: 'operational',
  description: '',
  amount: '',
  owner_company_id: '',
  category_id: '',
  cost_center_id: '',
  client_id: '',
  supplier_id: '',
  contract_id: '',
  vehicle_id: '',
  due_date: new Date().toISOString().substring(0, 10),
  competence_date: new Date().toISOString().substring(0, 10),
  payment_status: 'pending',
  payment_method_id: '',
  bank_account_id: '',
  generate_installments: false,
  total_installments: 1,
  notes: '',
  attachments: [],
};

export default function NewTransactionModal({ open, onOpenChange, onSuccess, defaultType = 'expense' }) {
  const [tab, setTab] = useState('rapido');
  const [form, setForm] = useState({ ...initialForm, type: defaultType });
  const [saving, setSaving] = useState(false);

  // Aux state
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(prev => ({ ...initialForm, type: defaultType, owner_company_id: prev.owner_company_id || '' }));
      loadAux();
    }
  }, [open, defaultType]);

  const loadAux = async () => {
    try {
      const [oc, cat, cc, veh, ctr, cli, sup, pm, ba] = await Promise.all([
        api.get('/masters/owner-companies', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/categories', { params: { type: 'financial', limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/cost-centers', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/vehicles', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
        api.get('/contracts', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
        api.get('/clients', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
        api.get('/suppliers', { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
        api.get('/masters/payment-methods', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } })),
        api.get('/financial/bank-accounts').catch(() => ({ data: { data: [] } })),
      ]);
      const extract = (r) => {
        const d = r?.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.items)) return d.items;
        if (Array.isArray(d?.data)) return d.data;
        return [];
      };
      const ocs = extract(oc);
      setOwnerCompanies(ocs);
      if (ocs.length > 0 && !form.owner_company_id) {
        setForm(f => ({ ...f, owner_company_id: ocs[0].id }));
      }
      setCategories(extract(cat));
      setCostCenters(extract(cc));
      setVehicles(extract(veh));
      setContracts(extract(ctr));
      setClients(extract(cli));
      setSuppliers(extract(sup));
      setPaymentMethods(extract(pm));
      setBankAccounts(extract(ba));
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 5MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm(f => ({
      ...f,
      attachments: [...f.attachments, { name: file.name, url, mime_type: file.type, size: file.size }]
    }));
    toast.success('Comprovante anexado');
  };

  const removeAttachment = (index) => {
    setForm(f => ({
      ...f,
      attachments: f.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error('Informe a descrição do lançamento'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Informe um valor válido'); return; }
    if (!form.owner_company_id) { toast.error('Selecione a empresa/CNPJ responsável'); return; }
    if (!form.due_date) { toast.error('Informe a data de vencimento'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        contract_id: form.contract_id || null,
        vehicle_id: form.vehicle_id || null,
        client_id: form.client_id || null,
        supplier_id: form.supplier_id || null,
        category_id: form.category_id || null,
        cost_center_id: form.cost_center_id || null,
        payment_method_id: form.payment_method_id || null,
        bank_account_id: form.bank_account_id || null,
      };

      await api.post('/financial/transactions', payload);
      toast.success(payload.generate_installments ? 'Parcelas lançadas com sucesso!' : 'Lançamento registrado com sucesso!');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center font-bold">
              <Plus className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg">Novo Lançamento Financeiro</DialogTitle>
              <DialogDescription className="text-xs">
                Registre receitas, despesas, custos imprevistos ou manutenções operacionais.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          {/* Seletor Tipo de Lançamento */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, type: 'expense', classification: 'operational' })}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-xs font-semibold transition-all ${
                form.type === 'expense' && form.classification === 'operational'
                  ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                  : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50'
              }`}
            >
              <ArrowDownRight className="h-4 w-4 text-red-600" />
              Despesa Operacional
            </button>

            <button
              type="button"
              onClick={() => setForm({ ...form, type: 'revenue', classification: 'operational' })}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-xs font-semibold transition-all ${
                form.type === 'revenue'
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50'
              }`}
            >
              <ArrowUpRight className="h-4 w-4 text-emerald-600" />
              Receita
            </button>

            <button
              type="button"
              onClick={() => setForm({ ...form, type: 'expense', classification: 'extraordinary' })}
              className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border text-xs font-semibold transition-all ${
                form.type === 'expense' && form.classification === 'extraordinary'
                  ? 'bg-amber-50 border-amber-500 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                  : 'bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50'
              }`}
            >
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Custo Imprevisto
            </button>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="rapido">Lançamento Rápido</TabsTrigger>
              <TabsTrigger value="completo">Lançamento Completo</TabsTrigger>
            </TabsList>

            {/* TAB RÁPIDO */}
            <TabsContent value="rapido" className="space-y-3 pt-3">
              <div className="space-y-1.5">
                <Label>Descrição *</Label>
                <Input
                  required
                  placeholder="Ex: Manutenção emergencial, Compra de peças, Viagem extra..."
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Valor (R$) *</Label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Empresa / CNPJ Responsável *</Label>
                  <Select
                    value={form.owner_company_id}
                    onValueChange={v => setForm({ ...form, owner_company_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                    <SelectContent>
                      {ownerCompanies.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.cnpj ? `(${c.cnpj})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Categoria</Label>
                  <Select
                    value={form.category_id || 'none'}
                    onValueChange={v => setForm({ ...form, category_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Data de Vencimento *</Label>
                  <Input
                    required
                    type="date"
                    value={form.due_date}
                    onChange={e => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>

            {/* TAB COMPLETO */}
            <TabsContent value="completo" className="space-y-3 pt-3">
              <div className="space-y-1.5">
                <Label>Descrição Detalhada *</Label>
                <Input
                  required
                  placeholder="Descrição da despesa ou receita"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Valor (R$) *</Label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5 md:col-span-2">
                  <Label>Empresa / CNPJ Responsável *</Label>
                  <Select
                    value={form.owner_company_id}
                    onValueChange={v => setForm({ ...form, owner_company_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione a empresa" /></SelectTrigger>
                    <SelectContent>
                      {ownerCompanies.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} {c.cnpj ? `(${c.cnpj})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Veículo Vinculado (Opcional)</Label>
                  <Select
                    value={form.vehicle_id || 'none'}
                    onValueChange={v => setForm({ ...form, vehicle_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione o veículo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum veículo</SelectItem>
                      {vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          Frota #{v.fleet_number || '-'} — {v.plate} ({v.model || v.brand})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Contrato Vinculado (Opcional)</Label>
                  <Select
                    value={form.contract_id || 'none'}
                    onValueChange={v => setForm({ ...form, contract_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione o contrato" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum contrato</SelectItem>
                      {contracts.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          Contrato #{c.number} — {c.client_name || 'Cliente'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {form.type === 'revenue' ? (
                  <div className="space-y-1.5">
                    <Label>Cliente</Label>
                    <Select
                      value={form.client_id || 'none'}
                      onValueChange={v => setForm({ ...form, client_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum cliente</SelectItem>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Fornecedor</Label>
                    <Select
                      value={form.supplier_id || 'none'}
                      onValueChange={v => setForm({ ...form, supplier_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Selecione o fornecedor" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum fornecedor</SelectItem>
                        {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.corporate_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label>Centro de Custo</Label>
                  <Select
                    value={form.cost_center_id || 'none'}
                    onValueChange={v => setForm({ ...form, cost_center_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione o centro de custo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      {costCenters.map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.name} ({cc.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Data de Competência</Label>
                  <Input
                    type="date"
                    value={form.competence_date}
                    onChange={e => setForm({ ...form, competence_date: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Data de Vencimento *</Label>
                  <Input
                    required
                    type="date"
                    value={form.due_date}
                    onChange={e => setForm({ ...form, due_date: e.target.value })}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Forma de Pagamento</Label>
                  <Select
                    value={form.payment_method_id || 'none'}
                    onValueChange={v => setForm({ ...form, payment_method_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione a forma" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {paymentMethods.map(pm => <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Conta Bancária</Label>
                  <Select
                    value={form.bank_account_id || 'none'}
                    onValueChange={v => setForm({ ...form, bank_account_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma</SelectItem>
                      {bankAccounts.map(b => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.account_name} ({b.bank_name || 'Banco'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Parcelamento */}
              <div className="p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50/60 dark:bg-neutral-900/40 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-neutral-800 dark:text-neutral-200">Parcelamento Automático</p>
                    <p className="text-[11px] text-neutral-500">Divide o valor em parcelas mensais consecutivas</p>
                  </div>
                  <Switch
                    checked={form.generate_installments}
                    onCheckedChange={v => setForm({ ...form, generate_installments: v })}
                  />
                </div>
                {form.generate_installments && (
                  <div className="pt-2 flex items-center gap-3">
                    <Label className="text-xs">Número de parcelas:</Label>
                    <Input
                      type="number"
                      min="2"
                      max="48"
                      className="w-24 h-8 text-xs font-semibold"
                      value={form.total_installments}
                      onChange={e => setForm({ ...form, total_installments: parseInt(e.target.value) || 2 })}
                    />
                    <span className="text-xs text-neutral-500">
                      {form.amount && parseFloat(form.amount) > 0 ? (
                        <>({form.total_installments}x de R$ {(parseFloat(form.amount) / (form.total_installments || 1)).toFixed(2)})</>
                      ) : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Anexos */}
              <div className="space-y-1.5">
                <Label>Comprovante / Nota Fiscal</Label>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="h-8 text-xs gap-1">
                    <Upload className="h-3.5 w-3.5" /> Anexar Arquivo
                  </Button>
                  <input ref={fileRef} type="file" onChange={handleFileUpload} className="hidden" />
                </div>
                {form.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {form.attachments.map((att, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">
                        {att.name}
                        <button type="button" onClick={() => removeAttachment(i)} className="text-red-500 hover:text-red-700 ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Observações</Label>
                <Textarea
                  rows={2}
                  placeholder="Informações adicionais sobre o lançamento..."
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">
              {saving ? 'Registrando...' : 'Registrar Lançamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
